import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Divider,
} from "@mui/material";
import { Tablecell, normalizeTickerForComparison } from "../../../component/Methods";

const SignalDifferenceSummary = ({ currentSignalsData, modifiedSignalsData }) => {
  const [CombinedArray, setCombinedArray] = useState([]);

  useEffect(() => {
    // Only show data when we have modified signals data
    if (modifiedSignalsData && modifiedSignalsData.length > 0 && currentSignalsData) {
      // Use the utility function to normalize ticker for comparison
      // This treats '.' and '/' as equivalent (e.g., TSLA.A = TSLA/A)
      const normalizeTicker = normalizeTickerForComparison;

      // Helper function to normalize long_short value
      const normalizeLongShort = (value, existingLongShort) => {
        // Always derive from value to ensure accuracy (value is the source of truth)
        // If value is negative, it's short; if positive or zero, it's long
        const numValue = parseFloat(value) || 0;
        return numValue >= 0 ? "L" : "S";
      };

      // Create maps for easier lookup
      const currentMap = new Map();
      const modifiedMap = new Map();

      // Build current signals map
      currentSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;

        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);

        currentMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Build modified signals map
      modifiedSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;

        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);

        modifiedMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Find all unique tickers from both sets
      const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

      // Calculate metrics for new summary format
      let newTickersCount = 0;
      let newTickersWeight = 0;
      let removedTickersCount = 0;
      let removedTickersWeight = 0;
      let consistentTickersCount = 0;
      let consistentTickersWeight = 0;
      let longWeightChange = 0;
      let shortWeightChange = 0;

      allTickers.forEach(ticker => {
        const currentSignal = currentMap.get(ticker);
        const modifiedSignal = modifiedMap.get(ticker);

        if (modifiedSignal && Math.abs(modifiedSignal.value || 0) < 0.0001) {
          // Modified signal exists but weight is 0 - treat as REMOVED
          removedTickersCount++;
          const currentValue = currentSignal ? parseFloat(currentSignal.value) || 0 : 0;
          removedTickersWeight += Math.abs(currentValue);
        } else if (currentSignal && modifiedSignal) {
          // Ticker exists in both - check if values changed
          const currentValue = parseFloat(currentSignal.value) || 0;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;

          // Check if any key attributes changed
          const valueChanged = Math.abs(currentValue - modifiedValue) > 0.0001;
          const assetTypeChanged = (currentSignal.assetType || "").trim() !== (modifiedSignal.assetType || "").trim();
          const longShortChanged = (currentSignal.long_short || "").toUpperCase() !== (modifiedSignal.long_short || "").toUpperCase();

          if (valueChanged || assetTypeChanged || longShortChanged) {
            // Position changed - calculate weight changes for long/short
            const weightDiff = modifiedValue - currentValue;

            // Add to long or short weight change based on position direction
            if (currentValue >= 0 || modifiedValue >= 0) {
              // Long position change
              longWeightChange += weightDiff;
            }
            if (currentValue < 0 || modifiedValue < 0) {
              // Short position change (shorts are negative, so weight change is opposite)
              shortWeightChange += weightDiff;
            }
          } else {
            // No change - consistent ticker
            consistentTickersCount++;
            consistentTickersWeight += Math.abs(modifiedValue);
          }
        } else if (!currentSignal && modifiedSignal) {
          // Only in modified signals - NEW ticker
          newTickersCount++;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;
          newTickersWeight += Math.abs(modifiedValue);

          // Add to long or short weight change
          if (modifiedValue >= 0) {
            longWeightChange += modifiedValue;
          } else {
            shortWeightChange += modifiedValue; // negative value
          }
        } else if (currentSignal && !modifiedSignal) {
          // Only in current signals - REMOVED ticker
          removedTickersCount++;
          const currentValue = parseFloat(currentSignal.value) || 0;
          removedTickersWeight += Math.abs(currentValue);

          // Subtract from long or short weight change
          if (currentValue >= 0) {
            longWeightChange -= currentValue;
          } else {
            shortWeightChange -= currentValue; // subtracting negative = adding positive
          }
        }
      });

      function roundit(number) {
        return number.toFixed(2).replace(/\.?0+$/, "");
      }

      const transformedArray = [
        { name: "New Tickers", value: newTickersCount },
        { name: "Removed Tickers", value: removedTickersCount },
        { name: "Consistent Tickers", value: consistentTickersCount },
        { name: "Long Weight Difference", value: roundit(longWeightChange) },
        { name: "Short Weight Difference", value: roundit(shortWeightChange) },
      ];

      setCombinedArray(transformedArray);
    } else {
      setCombinedArray([]);
    }
  }, [currentSignalsData, modifiedSignalsData]);

  const theme = useTheme();

  // Don't render anything if there's no modified signals data
  if (!modifiedSignalsData || modifiedSignalsData.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          border: `100px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "10px",
          width: "80%",
          marginRight: 1,
          minHeight: "120px",

        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: `1px solid ${theme.palette.divider}`,
            marginTop: "0px",
          }}
        >
          <TableBody>
            {CombinedArray?.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },

                  backgroundColor: "transparent",
                }}
              >
                <Tablecell
                  align="left"
                  sx={{
                    fontWeight: "900",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom:
                      row.name == "Consistent Tickers"
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                  }}
                >
                  {row?.name}
                </Tablecell>

                <Tablecell
                  align="right"
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    paddingLeft: "5px",
                    color: theme.palette.text.primary,
                    borderRight: "none",
                    borderLeft: "none",
                    borderTop: "none",
                    borderBottom:
                      row.name == "Consistent Tickers"
                        ? `1px solid ${theme.palette.divider}`
                        : "none",
                  }}
                >
                  {index < 3 ? row.value : `${row.value}%`}
                </Tablecell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
export default SignalDifferenceSummary;
